/**
 * Shared state for the assignment-centric NP triage step.
 *
 * The main-column panel and the right-side aside live in two sibling
 * subtrees under the app shell (the aside is handed to LayoutContext via
 * registerAside and rendered next to <Outlet/>, NOT inside the page), so
 * plain React context can't bridge them. Instead this module keeps a
 * tiny external store: the provider (rendered in the page) runs the data
 * hooks once and publishes their results + mutators + UI selection into
 * the store; both the panel and the aside subscribe via
 * useSyncExternalStore. There is only ever one np_selected stage mounted,
 * so a single module-level store is safe.
 */

import { ReactNode, useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';

import { useLayoutOptional } from '@/contexts/LayoutContext';
import { useBackofficeAssignments } from '@/hooks/useBackofficeAssignments';
import { useCommercialChanges } from '@/hooks/useCommercialChanges';
import { useReportRequestAttachments } from '@/hooks/useReportRequestAttachments';
import { useResearchPlan } from '@/hooks/useResearchPlan';
import type { BackofficeTemplate } from '@/types';
import type { ReportRequestAttachment } from '@/types/attachment';
import type { CommercialChange } from '@/types/commercial-change';
import type { BackofficeAssignment } from '@/types/np-assignments';
import type { ResearchPlanItemForPanel } from '@/types/research-plan';
import {
  buildTemplateTiles,
  tilesToTemplateItems,
  type TemplateTileJurisdiction,
  type TemplateTileSubject,
} from './templateTiles';

/** The unit vocabulary the picker groups: automated HUMAN plan items, or
 * legacy Django-template scope components. */
export type NpAssignmentsSource = 'research_plan' | 'template_components';

export interface NpAssignmentsContextValue {
  reportId: string | null;
  reportRequestId: string | null;
  /** The report is completed, so a change to a partner's fee or deadline needs a reason. */
  reportCompleted: boolean;
  /** The report has started: its project is active, so a partner can still be added. */
  reportStarted: boolean;
  /** One partner assignment's most recent change per field. */
  lastChangeFor: (assignmentId: string) => Record<string, CommercialChange>;
  /** Refresh the history after an edit. */
  refetchCommercialChanges: () => Promise<void>;
  assignments: BackofficeAssignment[];
  unassignedHumanItemIds: string[];
  ready: boolean;
  isLoading: boolean;
  humanItems: ResearchPlanItemForPanel[];
  /** Which unit vocabulary the grid is showing. */
  source: NpAssignmentsSource;
  /** Legacy flow: the report's subjects (with jurisdictions) — used by the
   * grid's "add jurisdiction" control to know which subjects can take one. */
  templateSubjects: TemplateTileSubject[];
  /** Legacy flow: add a jurisdiction to a subject, fanning out its tiles. */
  addJurisdiction: (subjectId: string, jurisdiction: TemplateTileJurisdiction) => void;
  /** Conversation files bound to the RR — the dropdown source for
   * attaching project documents to an assignment. */
  candidateFiles: ReportRequestAttachment[];
  actions: ReturnType<typeof useBackofficeAssignments>;
  /** Create an assignment from selected tile ids — branches on `source`
   * (plan-item ids vs template-component payloads). Returns the created
   * assignment or null. */
  createFromSelection: (tileIds: string[]) => Promise<BackofficeAssignment | null>;
  refetchPlan: () => Promise<void>;

  activeAssignmentId: string | null;
  setActiveAssignmentId: (id: string | null) => void;
  activeNpId: string | null;
  setActiveNpId: (id: string | null) => void;
  /** Select an assignment AND ensure the aside (NP shortlist + chat) is
   * open — so clicking "Find & message NPs" / an assignment card always
   * surfaces the shortlist even if the aside was collapsed. */
  selectAssignment: (assignmentId: string, npId?: string | null) => void;
  gridSelectionIds: string[];
  setGridSelectionIds: (ids: string[]) => void;
  gridCollapsed: boolean;
  setGridCollapsed: (collapsed: boolean) => void;
  colorForAssignment: (assignmentId: string) => string;
}

const ASSIGNMENT_COLORS = [
  'blue',
  'grape',
  'teal',
  'orange',
  'cyan',
  'pink',
  'lime',
  'indigo',
  'violet',
  'green',
];

// --- module-level store ---

let current: NpAssignmentsContextValue | null = null;
const listeners = new Set<() => void>();

function notify() {
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

interface NpAssignmentsProviderProps {
  reportId: string | null;
  reportRequestId: string | null;
  /** True once the report is completed. Defaults to false. */
  reportCompleted?: boolean;
  /** True once the report has started. Defaults to false. */
  reportStarted?: boolean;
  /** Unit vocabulary. Defaults to 'research_plan' (automated flow). For
   * 'template_components' (legacy flow) the page supplies the linked Django
   * `template` + the report's `subjects` (with their extracted jurisdictions),
   * and the research-plan fetch is skipped. */
  source?: NpAssignmentsSource;
  /** Legacy flow: the linked Backoffice template (its scope components). */
  template?: BackofficeTemplate | null;
  /** Legacy flow: the report's subjects with extracted jurisdictions. */
  templateSubjects?: TemplateTileSubject[];
  /** True while the template + subjects are still loading (legacy flow). */
  templateLoading?: boolean;
  /** Called once a lock, unlock, cancel or delete has gone through and the
   * assignments are re-read: the page re-reads whatever else follows the
   * lock (the registry-documents card's gate on the report or
   * report-request detail). */
  onLockChanged?: () => void | Promise<void>;
  children: ReactNode;
}

export function NpAssignmentsProvider({
  reportId,
  reportRequestId,
  reportCompleted = false,
  reportStarted = false,
  source = 'research_plan',
  template = null,
  templateSubjects,
  templateLoading = false,
  onLockChanged,
  children,
}: Readonly<NpAssignmentsProviderProps>) {
  const isTemplate = source === 'template_components';
  const actions = useBackofficeAssignments(reportId, { onLockChanged });
  const commercialChanges = useCommercialChanges(reportId);
  const rrAttachments = useReportRequestAttachments(reportRequestId ?? undefined);
  // Skip the research-plan fetch entirely for the legacy (template) source —
  // a legacy report has no plan items (plan-build was a no-op).
  const plan = useResearchPlan(isTemplate ? null : reportId);

  const [activeAssignmentId, setActiveAssignmentId] = useState<string | null>(null);
  const [activeNpId, setActiveNpId] = useState<string | null>(null);
  const [gridSelectionIds, setGridSelectionIds] = useState<string[]>([]);
  const [gridCollapsed, setGridCollapsed] = useState(false);
  // Analyst-added jurisdictions per subject (on top of the extracted ones) —
  // for the legacy flow's "add jurisdiction" control.
  const [addedJurisdictions, setAddedJurisdictions] = useState<
    Record<string, TemplateTileJurisdiction[]>
  >({});

  // Merge extracted + added jurisdictions per subject, then build the tiles.
  const templateTiles = useMemo<ResearchPlanItemForPanel[]>(() => {
    if (!isTemplate) {
      return [];
    }
    const subjects = (templateSubjects ?? []).map((s) => {
      const added = addedJurisdictions[s.id] ?? [];
      const seen = new Set(s.jurisdictions.map((j) => j.code));
      const merged = [...s.jurisdictions, ...added.filter((j) => !seen.has(j.code))];
      return { ...s, jurisdictions: merged };
    });
    return buildTemplateTiles(template, subjects);
  }, [isTemplate, template, templateSubjects, addedJurisdictions]);

  const addJurisdiction = useCallback(
    (subjectId: string, jurisdiction: TemplateTileJurisdiction) => {
      setAddedJurisdictions((prev) => {
        const existing = prev[subjectId] ?? [];
        if (existing.some((j) => j.code === jurisdiction.code)) {
          return prev;
        }
        return { ...prev, [subjectId]: [...existing, jurisdiction] };
      });
    },
    []
  );

  const setAsideOpen = useLayoutOptional()?.setAsideOpen;
  const selectAssignment = useCallback(
    (assignmentId: string, npId: string | null = null) => {
      setActiveAssignmentId(assignmentId);
      setActiveNpId(npId);
      // Force the aside open so the NP shortlist + chat is visible even if
      // the analyst had collapsed it.
      setAsideOpen?.(true);
    },
    [setAsideOpen]
  );

  const assignments = actions.data?.assignments ?? [];
  const unassignedHumanItemIds = actions.data?.unassignedHumanItemIds ?? [];
  const ready = actions.data?.ready ?? false;

  const createFromSelection = useCallback(
    (tileIds: string[]) => {
      if (isTemplate) {
        return actions.createTemplate(tilesToTemplateItems(tileIds, templateTiles ?? []));
      }
      return actions.create(tileIds);
    },
    [isTemplate, actions, templateTiles]
  );

  const humanItems = useMemo<ResearchPlanItemForPanel[]>(() => {
    if (isTemplate) {
      return templateTiles ?? [];
    }
    if (!plan.data) {
      return [];
    }
    return plan.data.groups.flatMap((g) => g.items.filter((i) => i.item_type === 'human'));
  }, [isTemplate, templateTiles, plan.data]);

  const colorByAssignment = useMemo(() => {
    const map = new Map<string, string>();
    assignments.forEach((a, idx) => {
      map.set(a.id, ASSIGNMENT_COLORS[idx % ASSIGNMENT_COLORS.length]);
    });
    return map;
  }, [assignments]);

  const value = useMemo<NpAssignmentsContextValue>(
    () => ({
      reportId,
      reportRequestId,
      reportCompleted,
      reportStarted,
      lastChangeFor: commercialChanges.lastForAssignment,
      refetchCommercialChanges: commercialChanges.refetch,
      assignments,
      unassignedHumanItemIds,
      ready,
      isLoading: actions.isLoading || plan.isLoading || templateLoading,
      humanItems,
      source,
      templateSubjects: templateSubjects ?? [],
      addJurisdiction,
      candidateFiles: rrAttachments.attachments,
      actions,
      createFromSelection,
      refetchPlan: plan.refetch,
      activeAssignmentId,
      setActiveAssignmentId,
      activeNpId,
      setActiveNpId,
      selectAssignment,
      gridSelectionIds,
      setGridSelectionIds,
      gridCollapsed,
      setGridCollapsed,
      colorForAssignment: (id: string) => colorByAssignment.get(id) ?? 'gray',
    }),
    [
      reportId,
      reportRequestId,
      reportCompleted,
      reportStarted,
      commercialChanges.lastForAssignment,
      commercialChanges.refetch,
      assignments,
      unassignedHumanItemIds,
      ready,
      actions,
      plan.isLoading,
      plan.refetch,
      templateLoading,
      source,
      templateSubjects,
      addJurisdiction,
      rrAttachments.attachments,
      createFromSelection,
      humanItems,
      activeAssignmentId,
      activeNpId,
      selectAssignment,
      gridSelectionIds,
      gridCollapsed,
      colorByAssignment,
    ]
  );

  // Populate the store synchronously during render so subscribers in this
  // subtree (the panel) see a value on their first read, before effects
  // run. Notifying listeners (re-rendering the aside subtree) happens in
  // the effect below — never during render.
  current = value;
  useEffect(() => {
    current = value;
    notify();
    return () => {
      if (current === value) {
        current = null;
        notify();
      }
    };
  }, [value]);

  return <>{children}</>;
}

const EMPTY_SNAPSHOT: NpAssignmentsContextValue | null = null;

export function useNpAssignmentsContext(): NpAssignmentsContextValue {
  const value = useSyncExternalStore(
    subscribe,
    () => current,
    () => EMPTY_SNAPSHOT
  );
  if (!value) {
    throw new Error('useNpAssignmentsContext used before NpAssignmentsProvider mounted');
  }
  return value;
}
